import type { VoiceConnectionState } from './voice-room-store';

/**
 * Pure state → presentation mapping for the unified voice pill (TC-33).
 *
 * There used to be TWO renderers for the same voice session: `VoiceRoom`
 * painted its own floating `DockFrame` pill (bottom-24) for the
 * connecting/reconnecting/error states while `CommsHub` painted the live
 * minimized pill (bottom-4) — duplicated controls and a pill that visually
 * "jumped" position when the connection came up. Now `VoiceRoom` only
 * PUBLISHES the connection state into the voice-room store and `CommsHub` is
 * the single renderer; this module keeps the mapping testable without React,
 * Zustand or LiveKit.
 */

/** Flags as tracked inside VoiceRoom → the single published connection state. */
export function deriveVoiceConnectionState(flags: {
  connected: boolean;
  reconnecting: boolean;
  error: string | null;
}): VoiceConnectionState {
  // Priority: a fatal error trumps everything; a LiveKit renegotiation
  // (which only happens while connected) trumps 'live'; otherwise the room is
  // either up or still on its way up.
  if (flags.error) return 'error';
  if (flags.reconnecting) return 'reconnecting';
  return flags.connected ? 'live' : 'connecting';
}

export type VoicePillView =
  | { kind: 'live' }
  | { kind: 'connecting'; channelName?: string }
  | { kind: 'reconnecting'; channelName?: string }
  | { kind: 'error' };

/** What the hub pill should say for a given connection state. */
export function buildVoicePillView(
  state: VoiceConnectionState,
  opts: { channelName?: string; errorMessage?: string } = {},
): VoicePillView {
  switch (state) {
    case 'connecting':
      return { kind: 'connecting', ...(opts.channelName?{channelName:opts.channelName}:{}) };
    case 'reconnecting':
      return { kind: 'reconnecting', ...(opts.channelName?{channelName:opts.channelName}:{}) };
    case 'error':
      return { kind: 'error' };
    default:
      return { kind: 'live' };
  }
}

/**
 * Should the hub show the transitional STATUS pill (as opposed to the normal
 * live pill / bolitas)?
 *
 * - connecting / error → always: these states have NO expanded overlay
 *   (VoiceRoom returns early before the full-screen UI), so the pill is the
 *   only indicator regardless of the minimized flag.
 * - reconnecting → only while minimized: the expanded overlay renders its own
 *   amber banner, a second pill would be redundant.
 * - live → never: the regular pill/bolitas logic applies.
 */
export function shouldShowVoiceStatusPill(
  state: VoiceConnectionState,
  minimized: boolean,
): boolean {
  if (state === 'connecting' || state === 'error') return true;
  if (state === 'reconnecting') return minimized;
  return false;
}
